
import { supabase } from '../lib/supabase';
import { logger } from '../lib/logger';

// Nombre de mots de passe conservés dans l'historique
const PASSWORD_HISTORY_SIZE = 5;

/**
 * Calcule le nombre de jours restants avant expiration du mot de passe
 */
export function getDaysUntilExpiry(
  passwordChangedAt: string | null | undefined,
  expiryDays: number = 90
): number {
  if (!passwordChangedAt) return 0;
  const changedDate = new Date(passwordChangedAt);
  const expiryDate = new Date(changedDate.getTime() + expiryDays * 24 * 60 * 60 * 1000);
  const now = new Date();
  const diff = expiryDate.getTime() - now.getTime();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

/**
 * Vérifie si le mot de passe est expiré
 */
export function isPasswordExpired(
  passwordChangedAt: string | null | undefined,
  expiryDays: number = 90
): boolean {
  return getDaysUntilExpiry(passwordChangedAt, expiryDays) <= 0;
}

/**
 * Vérifie si l'alerte d'expiration doit être affichée (< 14 jours)
 */
export function shouldShowExpiryWarning(
  passwordChangedAt: string | null | undefined,
  expiryDays: number = 90
): boolean {
  const daysLeft = getDaysUntilExpiry(passwordChangedAt, expiryDays);
  return daysLeft > 0 && daysLeft <= 14;
}

/**
 * Hache un mot de passe côté client pour comparaison d'historique
 * Utilise SHA-256 via Web Crypto API
 */
async function hashPassword(password: string): Promise<string> {
  try {
    const encoder = new TextEncoder();
    const data = encoder.encode(password);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  } catch (e) {
    // En cas d'échec du Web Crypto API, retourner une chaîne vide pour éviter le plantage
    logger.error('Erreur lors du hash du mot de passe:', e);
    return '';
  }
}

/**
 * Vérifie si le mot de passe a déjà été utilisé dans l'historique
 */
export async function isPasswordInHistory(
  password: string,
  history: string[]
): Promise<boolean> {
  const hashed = await hashPassword(password);
  if (!hashed) return false; // si le hash a échoué, on considère qu'il n'est pas dans l'historique
  return history.includes(hashed);
}

/**
 * Met à jour le mot de passe avec gestion de l'historique
 * Retourne { success, error }
 */
export async function changePasswordWithHistory(
  userId: string,
  newPassword: string,
  currentHistory: string[] = []
): Promise<{ success: boolean; error?: string }> {
  try {
    // 1. Vérifier que le mot de passe n'est pas dans l'historique
    const inHistory = await isPasswordInHistory(newPassword, currentHistory);
    if (inHistory) {
      return {
        success: false,
        error: `Ce mot de passe a déjà été utilisé récemment. Veuillez en choisir un différent (les ${PASSWORD_HISTORY_SIZE} derniers mots de passe sont mémorisés).`,
      };
    }

    // 2. Mettre à jour le mot de passe dans Supabase Auth
    // Promise.race pour éviter un loader infini si le client est bloqué
    const timeoutError = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('La requête a expiré. Veuillez réessayer.')), 15000)
    );
    const { error: authError } = await Promise.race([
      supabase.auth.updateUser({ password: newPassword }),
      timeoutError,
    ]);
    if (authError) throw authError;

    // 3. Hacher le nouveau mot de passe pour l'historique
    const hashedNew = await hashPassword(newPassword);
    if (!hashedNew) {
      throw new Error('Impossible de hacher le nouveau mot de passe.');
    }

    // 4. Mettre à jour l'historique (garder les N derniers)
    const updatedHistory = [hashedNew, ...currentHistory].slice(0, PASSWORD_HISTORY_SIZE);

    // 5. Mettre à jour la table users
    const { error: dbError } = await supabase
      .from('users')
      .update({
        password_changed_at: new Date().toISOString(),
        password_history: updatedHistory,
        must_change_password: false,
        updated_at: new Date().toISOString(),
      })
      .eq('id', userId);

    if (dbError) throw dbError;

    return { success: true };
  } catch (err: any) {
    return {
      success: false,
      error: err.message || 'Erreur lors du changement de mot de passe',
    };
  }
}

/**
 * Récupère les infos de politique de mot de passe d'un utilisateur
 */
export async function getPasswordPolicyInfo(userId: string): Promise<{
  passwordChangedAt: string | null;
  passwordHistory: string[];
  passwordExpiryDays: number;
  daysUntilExpiry: number;
  isExpired: boolean;
  showWarning: boolean;
} | null> {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('password_changed_at, password_history, password_expiry_days')
      .eq('id', userId)
      .maybeSingle();

    if (error || !data) return null;

    const expiryDays = data.password_expiry_days ?? 90;
    const changedAt = data.password_changed_at;
    const history = (data.password_history as string[]) ?? [];

    return {
      passwordChangedAt: changedAt,
      passwordHistory: history,
      passwordExpiryDays: expiryDays,
      daysUntilExpiry: getDaysUntilExpiry(changedAt, expiryDays),
      isExpired: isPasswordExpired(changedAt, expiryDays),
      showWarning: shouldShowExpiryWarning(changedAt, expiryDays),
    };
  } catch (e) {
    logger.error('Erreur lors de la récupération des politiques de mot de passe:', e);
    return null;
  }
}
